import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Calculator, FileText } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const SAPvsEPC = () => {
  return (
    <>
      <Helmet>
        <title>SAP vs EPC Explained | What's the Difference? | Energy Performance Direct</title>
        <meta name="description" content="Understanding the difference between SAP calculations and EPCs. When you need each, how they relate, costs, and what they mean for new builds vs existing properties." />
        <meta name="keywords" content="SAP vs EPC, SAP calculation explained, EPC vs SAP, new build SAP, existing property EPC, RdSAP, energy assessment difference" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/resources/sap-vs-epc" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8">
                <Link to="/resources" className="text-green-600 hover:underline text-sm">← Back to Resources</Link>
              </div>
              
              <header className="mb-12">
                <span className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">Technical</span>
                <h1 className="text-4xl font-bold text-gray-900 mt-4 mb-6">SAP vs EPC Explained: What's the Difference?</h1>
                <p className="text-xl text-gray-600">SAP and EPC are closely related but serve different purposes. This guide explains when you need each and how they work together.</p>
              </header>

              <section className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold mt-8 mb-4">Quick Summary</h2>
                <div className="grid md:grid-cols-2 gap-4 my-6">
                  <Card className="border-blue-200 bg-blue-50">
                    <CardContent className="pt-6">
                      <Calculator className="h-8 w-8 text-blue-600 mb-3" />
                      <h3 className="font-bold text-lg mb-2">SAP (Standard Assessment Procedure)</h3>
                      <p className="text-gray-700 text-sm mb-3">The <strong>calculation methodology</strong> used to assess energy performance of dwellings.</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Required for new builds</li>
                        <li>• Done from plans/specifications</li>
                        <li>• Produces SAP rating (1-100+)</li>
                        <li>• Used for Part L compliance</li>
                      </ul>
                    </CardContent>
                  </Card>
                  <Card className="border-green-200 bg-green-50">
                    <CardContent className="pt-6">
                      <FileText className="h-8 w-8 text-green-600 mb-3" />
                      <h3 className="font-bold text-lg mb-2">EPC (Energy Performance Certificate)</h3>
                      <p className="text-gray-700 text-sm mb-3">The <strong>certificate</strong> that communicates energy performance to owners, buyers and tenants.</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Required for sales/lettings</li>
                        <li>• Based on SAP or RdSAP</li>
                        <li>• Shows A-G rating</li>
                        <li>• Valid for 10 years</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">SAP Calculation Explained</h2>
                <p className="text-gray-700 mb-4">
                  SAP (Standard Assessment Procedure) is the UK Government's methodology for calculating the energy performance of dwellings. It's been the national calculation method since 1995 and is regularly updated (current version is SAP 10).
                </p>
                <p className="text-gray-700 mb-4">
                  SAP calculates energy use based on the building's physical characteristics:
                </p>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> Building fabric (walls, floors, roof, windows)</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> Heating and hot water systems</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> Ventilation strategy</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> Lighting</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> Renewable energy systems</li>
                </ul>
                <p className="text-gray-700 mb-4">
                  The calculation uses standardised assumptions about occupancy, climate and usage patterns, making it possible to compare different dwellings fairly.
                </p>

                <h2 className="text-2xl font-bold mt-8 mb-4">When Do You Need a SAP Calculation?</h2>
                <p className="text-gray-700 mb-4">SAP calculations are required for:</p>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" /> <strong>New build dwellings</strong> - to demonstrate Part L Building Regulations compliance</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" /> <strong>Conversions to dwellings</strong> - e.g., barn conversions, office-to-residential</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" /> <strong>Extensions</strong> - in some cases where Building Control requires it</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" /> <strong>Material change of use</strong> - creating new dwellings from non-residential</li>
                </ul>

                <h2 className="text-2xl font-bold mt-8 mb-4">RdSAP: The Simplified Version</h2>
                <p className="text-gray-700 mb-4">
                  RdSAP (Reduced Data SAP) is a simplified version of SAP used for existing properties. When you get an EPC for an existing home, the assessor uses RdSAP methodology.
                </p>
                <p className="text-gray-700 mb-4">
                  The key differences are:
                </p>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li><strong>RdSAP</strong> uses survey data and default assumptions where information isn't available</li>
                  <li><strong>Full SAP</strong> uses exact specifications from plans and construction details</li>
                </ul>

                <h2 className="text-2xl font-bold mt-8 mb-4">The Relationship Between SAP and EPC</h2>
                <div className="bg-gray-50 border rounded-lg p-6 my-6">
                  <h3 className="font-semibold mb-4">How They Connect:</h3>
                  <ol className="space-y-3 text-gray-700">
                    <li><strong>1. New Build:</strong> SAP calculation → Generates EPC (from SAP data)</li>
                    <li><strong>2. Existing Property:</strong> RdSAP survey → Generates EPC (from RdSAP data)</li>
                  </ol>
                  <p className="mt-4 text-gray-600 text-sm">The EPC is essentially the "output" that presents the SAP/RdSAP results in a standardised, user-friendly format.</p>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">Cost Comparison</h2>
                <div className="overflow-hidden rounded-lg border my-6">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Service</th>
                        <th className="px-4 py-3 text-left font-semibold">Typical Cost</th>
                        <th className="px-4 py-3 text-left font-semibold">When Needed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr>
                        <td className="px-4 py-3">EPC (existing home)</td>
                        <td className="px-4 py-3">£60-£120</td>
                        <td className="px-4 py-3">Selling or letting</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3">SAP Calculation (new build)</td>
                        <td className="px-4 py-3">£100-£300</td>
                        <td className="px-4 py-3">Design stage + completion</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3">Commercial EPC</td>
                        <td className="px-4 py-3">£180+</td>
                        <td className="px-4 py-3">Selling or letting commercial</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">Related Services</h2>
                <div className="grid md:grid-cols-2 gap-4 my-6">
                  <Link to="/sap-calculations" className="block">
                    <Card className="h-full hover:shadow-lg transition-shadow hover:border-green-300">
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">SAP Calculations</h3>
                        <p className="text-gray-600 text-sm">Design stage and as-built SAP for new builds</p>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/resources/epc-explained" className="block">
                    <Card className="h-full hover:shadow-lg transition-shadow hover:border-green-300">
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">EPC Guide</h3>
                        <p className="text-gray-600 text-sm">Complete guide to Energy Performance Certificates</p>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              </section>

              <div className="mt-12 p-6 bg-green-50 rounded-lg text-center">
                <h3 className="text-xl font-bold mb-3">Need a SAP Calculation or EPC?</h3>
                <p className="text-gray-600 mb-4">We provide both services across the South West.</p>
                <Link to="/booking">
                  <Button className="bg-green-600 hover:bg-green-700">
                    Get a Quote <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
};

export default SAPvsEPC;
